
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { SMELessonService } from '../../../services/sme-lesson.service';
import { AuthService } from '../../../services/auth.service';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonApiService } from '@app/services/common-api.service';
import { TOCService } from '@app/services/toc.service';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';
import { triggerGeneratedLessonPptDownload } from '@app/utils/lesson-ppt-download';
import { environment } from '@env/environment';

/** Lesson row from lessonsList (dashboard cards). */
type DashboardLesson = {
  lessonId: number;
  lessonTitle?: string;
  lessonStatus?: string;
  linkedToProject?: number | boolean;
};

@Component({
  selector: 'app-sme-dashboard',
  templateUrl: './smedashboard.component.html',
  styleUrls: ['./smedashboard.component.scss']
})
export class SMEDashboardComponent implements OnInit {

  lessons: any[] = [];
  loading = false;
  pptGeneratingLessonId: number | null = null;
  lessonLifecycleFilter: '' | 'IN_PROGRESS' | 'RELEASED' | 'ARCHIVED' = '';
  userid = this.commonAPI.userInfo.userID // Replace with actual user ID retrieval logic

  @ViewChild('scrollContainer', { static: false })
  scrollContainer!: ElementRef;

  constructor(
    private lessonService: SMELessonService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private commonAPI: CommonApiService,
    private tocService: TOCService,
  ) { }

  isLessonLinkedToProject(lesson: DashboardLesson): boolean {
    return Number(lesson?.linkedToProject) === 1 || lesson?.linkedToProject === true;
  }

  canClearLesson(lesson: DashboardLesson): boolean {
    if (this.isLessonLinkedToProject(lesson)) return false;
    const st = String(lesson?.lessonStatus || 'EMPTY').toUpperCase();
    return st !== 'EMPTY';
  }

  clearLessonDisabledTitle(lesson: DashboardLesson): string {
    if (this.isLessonLinkedToProject(lesson)) {
      return 'Cannot clear: lesson is linked to a creator project';
    }
    const st = String(lesson?.lessonStatus || 'EMPTY').toUpperCase();
    if (st === 'EMPTY') {
      return 'No TOC or slides to clear yet';
    }
    return 'Clear TOC, slides, and assets; keep lesson record';
  }

  deleteLessonDisabledTitle(lesson: DashboardLesson): string {
    if (this.isLessonLinkedToProject(lesson)) {
      return 'Cannot delete: lesson is linked to a creator project';
    }
    return 'Permanently delete this lesson';
  }

  clearLessonContent(lesson: DashboardLesson): void {
    if (this.isLessonLinkedToProject(lesson)) {
      alert('This lesson is linked to a creator project. Unlink it before clearing content.');
      return;
    }
    if (
      !window.confirm(
        `Clear all generated content for "${lesson.lessonTitle || 'this lesson'}"? The lesson record is kept; TOC, slides, assets, and references are removed.`,
      )
    ) {
      return;
    }
    this.tocService.clearLessonContent(this.userid, lesson.lessonId).subscribe({
      next: () => {
        this.loadLessons();
        this.router.navigate(['/SME/chat'], {
          queryParams: { lessonId: lesson.lessonId, generateToc: '1' },
        });
      },
      error: (err: unknown) => alert(extractApiErrorMessage(err, 'Failed to clear lesson content.')),
    });
  }

  deleteLesson(lesson: DashboardLesson): void {
    if (this.isLessonLinkedToProject(lesson)) {
      alert('This lesson is linked to a creator project. Unlink it before deleting.');
      return;
    }
    if (
      !window.confirm(
        `Delete lesson "${lesson.lessonTitle || lesson.lessonId}" permanently? This cannot be undone.`,
      )
    ) {
      return;
    }
    this.lessonService.deleteLesson(this.userid, lesson.lessonId).subscribe({
      next: () => this.loadLessons(),
      error: (err: unknown) => alert(extractApiErrorMessage(err, 'Failed to delete lesson.')),
    });
  }
  logout(){
    console.log("logging out");
    // Remove the token and any stored user info
    localStorage.removeItem('token'); // or sessionStorage, depending on where you store it
    localStorage.removeItem('user');  // if you store user info
    console.log("Cleaned");
    // Redirect to login page
//   this.router.navigate(['/login']);
this.router.navigate(['/login']);
  }

  ngOnInit(): void {
    //this.userid = Number(this.route.snapshot.paramMap.get('userid'));
    console.log(this.route.snapshot);
    this.route.paramMap.subscribe(params => {
      console.log(this.authService.getUser());
      this.userid = this.authService.getUser();
    });
    console.log("initi with");
    console.log(this.userid);

    this.loadLessons();
  }

  loadLessons() {
    this.loading = true;
    const filt = this.lessonLifecycleFilter?.trim() || null;
    this.lessonService.getLessons(this.userid, filt).subscribe({
      next: (res) => {
        console.log("result was   : " + JSON.stringify(res));
        this.lessons = res;
        console.log(this.lessons);
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  addLesson() {
    console.log("navigating to new lesson for user id " + this.userid);
    this.router.navigate(['/SME/lesson/new/userid', this.userid]);
  }

  goChatMode() {
    this.router.navigate(['/SME/chat']);
  }

  editLesson(lessonId: number) {
    this.router.navigate(['/SME/lesson/meta/lessonid', lessonId]);
  }

  generatePPT(lessonId: number) {
    this.pptGeneratingLessonId = lessonId;
    this.lessonService.generatePPT(this.userid, lessonId, 'REVIEW').subscribe({
      next: (data: unknown) => {
        triggerGeneratedLessonPptDownload(
          this.commonAPI,
          `${environment.baseUrl}/lessonMgmt`,
          this.userid,
          lessonId,
          data,
          (ok, message) => {
            if (!ok) {
              alert(message);
            }
          },
        );
        this.pptGeneratingLessonId = null;
        this.loadLessons();
      },
      error: (err: unknown) => {
        this.pptGeneratingLessonId = null;
        alert(extractApiErrorMessage(err, 'PPT generation failed.'));
        this.loadLessons();
      },
    });
  }

  onLessonLifecycleChange(lesson: any, status: string) {
    this.lessonService.updateLessonLifecycle(this.userid, lesson.lessonId, status).subscribe({
      next: () => {
        lesson.lessonLifecycleStatus = status;
        this.loadLessons();
      },
      error: (err) =>
        alert(extractApiErrorMessage(err, 'Failed to update lesson workflow status.')),
    });
  }

  copyLesson(lessonId: number) {
    this.lessonService.copyLesson(this.userid, lessonId).subscribe(() => {
      this.loadLessons()
    });
  }

  scrollLeft() {
    this.scrollContainer.nativeElement.scrollBy({
      left: -300,
      behavior: 'smooth'
    });
  }

  scrollRight() {
    this.scrollContainer.nativeElement.scrollBy({
      left: 300,
      behavior: 'smooth'
    });
  }
}
